import { useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import PauseCircleFilledRoundedIcon from "@mui/icons-material/PauseCircleFilledRounded";

type InstructionAudioButtonProps = {
  audioUrl?: string;
  label?: string;
  disabled?: boolean;
};

export function InstructionAudioButton({
  audioUrl = "",
  label = "Play instructions audio",
  disabled = false,
}: InstructionAudioButtonProps) {
  const normalizedAudioUrl = audioUrl.trim();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  useEffect(() => {
    if (!normalizedAudioUrl) {
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
      audioRef.current.addEventListener("ended", () => setIsPlaying(false));
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));
      audioRef.current.addEventListener("play", () => setIsPlaying(true));
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.src !== normalizedAudioUrl) {
      audio.pause();
      audio.preload = "auto";
      audio.src = normalizedAudioUrl;
      audio.load();
    }
  }, [normalizedAudioUrl]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (!disabled) {
      return;
    }

    audioRef.current?.pause();
  }, [disabled]);

  if (!normalizedAudioUrl) {
    return null;
  }

  const showAsPlaying = !disabled && isPlaying;

  const handleToggleAudio = async () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
      audioRef.current.preload = "auto";
      audioRef.current.addEventListener("ended", () => setIsPlaying(false));
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));
      audioRef.current.addEventListener("play", () => setIsPlaying(true));
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.src !== normalizedAudioUrl) {
      audio.pause();
      audio.preload = "auto";
      audio.src = normalizedAudioUrl;
      audio.load();
    }

    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        setIsPlaying(false);
        console.error("Failed to play instruction audio:", error);
      }
      return;
    }

    audio.pause();
    audio.currentTime = 0;
  };

  return (
    <IconButton
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (disabled) {
          return;
        }
        void handleToggleAudio();
      }}
      sx={{
        width: 36,
        height: 36,
        backgroundColor: "primary.light",
        color: "primary.dark",
        borderColor: "primary.main",
        "&:hover": {
          backgroundColor: "primary.light",
          opacity: 0.9,
        },
      }}
    >
      {showAsPlaying ? (
        <PauseCircleFilledRoundedIcon sx={{ fontSize: 36 }} />
      ) : (
        <PlayCircleFilledRoundedIcon sx={{ fontSize: 36 }} />
      )}
    </IconButton>
  );
}
